import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';

import { useAnalytics } from 'src/components/settings/analytics';

import { messages } from '../messages';

type Props = {
  isOpen: boolean;
  testUuids: string[];
  isPending: boolean;
  onClose: () => void;
  onConfirm: (testUuids: string[]) => void;
};

export const RemoveBulkTestCasesModal = ({
  isOpen,
  testUuids,
  isPending,
  onClose,
  onConfirm,
}: Props) => {
  const { formatMessage } = useIntl();
  const { publishButtonClickEvent } = useAnalytics({
    source: 'removeBulkTestCasesModal',
  });

  const count = testUuids.length;

  const onModalClose = useCallback(() => {
    if (isPending) return;
    onClose();
  }, [isPending, onClose]);

  const onRemoveConfirm = useCallback(() => {
    publishButtonClickEvent('confirmBulkRemoveTestCases', { count });
    onConfirm(testUuids);
  }, [publishButtonClickEvent, onConfirm, testUuids, count]);

  return (
    <ModalTransition>
      {isOpen && (
        <Modal
          onClose={onModalClose}
          testId="bulk-remove-test-cases-confirm-modal"
        >
          <ModalHeader>
            <ModalTitle>
              {formatMessage(messages.bulkRemoveTestsModalTitle, { count })}
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            {formatMessage(messages.bulkRemoveTestsModalBody, { count })}
          </ModalBody>
          <ModalFooter>
            <Button
              appearance="subtle"
              onClick={onModalClose}
              isDisabled={isPending}
            >
              {formatMessage(messages.removeTestCancelButton)}
            </Button>
            <Button
              appearance="primary"
              onClick={onRemoveConfirm}
              isLoading={isPending}
              autoFocus
            >
              {formatMessage(messages.removeTestConfirmButton)}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
